import React, { Component } from 'react';
import styles from './index.css';
import { browser } from '@src/slide/infrastructure/module/independent/agent/common';
import TextEditItems from './text-edit-items-m';
import AddEditItems from './add-edit-items-m';
import classnames from 'classnames';
import { SoftKeyboard } from '@tencent/docs-soft-keyboard';
import { ColorPanelType, EditPanelType, getMobileEditPanelHeight } from './const';
import { IItemData } from './add-edit-items-m/item';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { IMobileEditorAppServices } from '@src/slide/application/entry/toc/editor/mobile/mobile-editor-app-service.interface';
import ColorPanel from '@com-m/edit-panel-m/text-edit-items-m/color-panel';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';
import { EditLayoutSuggestionContainer } from '@com-m/edit-panel-m/layout-suggestion';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IMobileRatingService } from '@src/slide/application/service/slide-rating-and-improve/mobile-slide-rating-service.interface';
import { DOMId } from '@editor/src/page/index/components/canvas/canvas-constants';
import { IColorPickerManager } from '@src/slide/application/service/color-picker-manager/color-picker-manager.interface';
import { ReplaceSlideAIMobileContainer } from './replace-slide-ai/replace-slide-ai';

interface IEditPanelProps {
    editItemDatas: Array<Record<string, unknown>>;
    insertItemDatas: Array<IItemData>;
    addonItemDatas: Array<IItemData>;
    type: string;
    colorPanelType: ColorPanelType;
    getRatingService(): IDynamicService<IMobileRatingService> | undefined;
}

export class MobileEditPanel extends Component<IEditPanelProps> {
    private softKeyboard: SoftKeyboard;
    private cacheTouchMatrix: { x: number; y: number } | null;

    public render() {
        const { editItemDatas, insertItemDatas, addonItemDatas, type, colorPanelType } = this.props;
        if (!editItemDatas?.length && type === EditPanelType.TEXT_EDIT) {
            this.closeEditPanel();
            return null;
        }
        const showAddPanel = type === EditPanelType.ADD_ADIT;
        const showTextPanel = type === EditPanelType.TEXT_EDIT && colorPanelType === ColorPanelType.NONE;
        const showColorPane = type === EditPanelType.TEXT_EDIT && colorPanelType !== ColorPanelType.NONE;
        const showLayoutSuggest = type === EditPanelType.LAYOUT_SUGGESTION;
        const replaceSlideAI = type === EditPanelType.AI_REPLACE_SLIDE;

        const containderCls = classnames({
            [styles['mobile-edit-panel-container']]: true,
            [styles['mobile-edit-panel-container-dom-mode']]: false,
        });
        const editContainerCls = classnames({
            [styles['mobile-edit-panel-container-inner']]: true,
            [styles['mobile-edit-inner-show']]: showTextPanel,
            [styles['mobile-edit-inner-right']]: !showTextPanel,
        });
        const addContainerCls = classnames({
            [styles['mobile-edit-panel-container-inner']]: true,
            [styles['mobile-edit-inner-show']]: showAddPanel,
            [styles['mobile-edit-inner-left']]: !showAddPanel,
        });
        const colorPanelContainerCls = classnames({
            [styles['mobile-edit-panel-container-color-panel-inner']]: true,
            [styles['mobile-edit-inner-show']]: showColorPane,
            [styles['mobile-edit-inner-right']]: !showColorPane,
        });
        return (
            <div
                className={containderCls}
                id="mobile-edit-panel-container"
                style={{
                    // 移动端且横屏 则减少编辑面板的高度
                    height: getMobileEditPanelHeight(),
                }}
            >
                <div className={editContainerCls} onMouseDown={browser.isPad ? this.preventDefault : undefined}>
                    <TextEditItems
                        itemDatas={editItemDatas}
                        menuManager={this.getMenuManager()}
                        openColorPanel={this.openColorPanel}
                        colorPickerManager={this.getColorPickerManager()}
                    />
                </div>
                <div className={addContainerCls}>
                    <AddEditItems insertItemDatas={insertItemDatas} addonItemDatas={addonItemDatas} />
                </div>
                <div className={colorPanelContainerCls}>
                    <ColorPanel colorPanelType={colorPanelType} closeColorPanel={this.closeColorPanel} />
                </div>
                {showLayoutSuggest && <EditLayoutSuggestionContainer getRatingService={this.props.getRatingService} />}
                {replaceSlideAI && (
                    <ReplaceSlideAIMobileContainer
                        onClose={() => this.closeEditPanel()}
                        startCurrentId={
                            this.getAppServices().viewableServiceLayer.selectionService.currentSlide?.id ?? ''
                        }
                        uiDataService={this.getAppServices().getUiDataService()}
                        aiAssistantService={this.getAppServices().editableServiceLayer?.aiAssistantService}
                    />
                )}
            </div>
        );
    }

    public componentDidMount(): void {
        const canvasAreaDom = document.getElementById(DOMId.CANVAS_WRAPPER);
        canvasAreaDom?.addEventListener('touchend', this.handleTouchEnd);
        canvasAreaDom?.addEventListener('touchstart', this.handleTouchStart);
        this.initSoftKeyBoard();
    }

    public componentWillUnmount(): void {
        const canvasAreaDom = document.getElementById(DOMId.CANVAS_WRAPPER);
        canvasAreaDom?.removeEventListener('touchend', this.handleTouchEnd);
        canvasAreaDom?.removeEventListener('touchstart', this.handleTouchStart);
    }

    private handleTouchStart = (e) => {
        const touch = e?.touches?.[0];
        if (touch) {
            this.cacheTouchMatrix = {
                x: touch.clientX,
                y: touch.clientY,
            };
        }
    };

    private handleTouchEnd = (e) => {
        const flag = (this.getAppServices().viewableServiceLayer.addonManager.addonHandler as any)
            ?.mobileClosePanelFlag;
        if (flag) {
            return;
        }
        if (this.isTouchEventMoved(e?.changedTouches?.[0])) {
            return;
        }
        this.closeEditPanel();
    };

    private preventDefault = (event: React.MouseEvent) => {
        // ipad app 中阻止默认事件，避免让文本框失焦
        event.preventDefault();
    };

    private closeEditPanel = () => {
        const serviceLayer = this.getAppServices().editableServiceLayer;

        if (serviceLayer?.mobileToolbarEditManager?.editPanelShowState) {
            serviceLayer.mobileToolbarEditManager.closeEditPanel();
        }
    };
    private initSoftKeyBoard = () => {
        this.softKeyboard = new SoftKeyboard();
        this.softKeyboard.on('change', keyboard => this.onKeyboardChange(keyboard));
        eventBus.on(EventKeys.IOS_KEY_BOARD_SHOW, () => {
            this.onKeyBoardShow();
        });
        eventBus.on(EventKeys.IOS_KEY_BOARD_HIDE, () => {
            this.onKeyBoardHide();
        });
    };
    private onKeyboardChange = (keyboard) => {
        if (browser.isAndroid) {
            const height = keyboard.heightInViewport || keyboard.heightOutOfViewport;
            if (height === 0) {
                this.onKeyBoardHide();
            }
            if (height !== 0) {
                this.onKeyBoardShow();
            }
        }
    };

    private onKeyBoardHide = () => {
        this.getAppServices().editableServiceLayer?.mobileToolbarEditManager.onKeyBoardHide();
    };

    private onKeyBoardShow = () => {
        this.getAppServices().editableServiceLayer?.mobileToolbarEditManager.onKeyBoardShow();
    };

    private isTouchEventMoved = (endTouch) => {
        if (!endTouch || !this.cacheTouchMatrix) {
            return false;
        }
        if (Math.abs(endTouch.clientX - this.cacheTouchMatrix.x) > 2) {
            return true;
        }
        if (Math.abs(endTouch.clientY - this.cacheTouchMatrix.y) > 2) {
            return true;
        }
        return false;
    };

    private getAppServices(): IMobileEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as IMobileEditorAppServices;
    }

    private getMenuManager() {
        return this.getAppServices().editableServiceLayer?.menuManager;
    }

    private getColorPickerManager(): IColorPickerManager | undefined {
        return this.getAppServices().editableServiceLayer?.colorPickerManager;
    }

    private openColorPanel = (type: ColorPanelType) => {
        const mobileToolbarEditManager = this.getAppServices().editableServiceLayer?.mobileToolbarEditManager;
        mobileToolbarEditManager?.setColorPanelType(type);
    };

    private closeColorPanel = () => {
        const mobileToolbarEditManager = this.getAppServices().editableServiceLayer?.mobileToolbarEditManager;
        mobileToolbarEditManager?.setColorPanelType(ColorPanelType.NONE);
    };
}
